import { useCallback, useEffect, useMemo, useState } from "react";

/**
 * PEDIR REUNIÃO COM O DESENVOLVIMENTO — e acompanhar a resposta.
 *
 * A pessoa escolhe um dos horários livres da agenda do Desenvolvimento, conta o
 * assunto e manda. O pedido fica "aguardando resposta" (o horário já fica
 * reservado para ela) até o desenvolvedor aceitar ou recusar — e a resposta
 * aparece aqui, em "Meus pedidos", e num aviso flutuante (AvisoReuniao) em
 * qualquer tela do sistema.
 *
 * Tudo passa pelo SERVIDOR deste sistema (server/solicitar-atualizacao.ts), que
 * repassa ao Desenvolvimento com o segredo do canal — o navegador nunca vê esse
 * segredo. Quem pede vem do login (header), não de um campo digitável.
 *
 * O arquivo é o MESMO em todos os sistemas e é autossuficiente de propósito
 * (como o EmDesenvolvimentoView): traz o próprio CSS, com as cores da marca, e
 * reconhece o modo escuro pelos dois padrões da casa ([data-theme="dark"] e .dark).
 */

const API_PADRAO = "/api/solicitar-atualizacao/reuniao";

/** Como falar com o servidor deste sistema. O padrão serve aos sistemas Bun
 *  (login no header); o Propostas passa o endereço e o token dele. */
interface Conexao {
  usuario: string;
  base?: string;
  cabecalhos?: () => Record<string, string>;
}

interface Dia {
  data: string;
  horarios: string[];
}

interface Pedido {
  id: number;
  titulo: string;
  pauta: string | null;
  data: string;
  hora: string | null;
  duracao_min: number | null;
  status: string;
  resposta: string | null;
  respondido_em: string | null;
  local: string | null;
  link: string | null;
  passou: boolean;
}

const CSS = `
.preu {
  --preu-card: #ffffff; --preu-borda: #dde8e1; --preu-texto: #13241b; --preu-suave: #5e7268;
  --preu-fundo2: #f4f9f6; --preu-verde: #0c7a3f; --preu-verde-claro: #e3f3ea; --preu-erro: #b42318;
  display: flex; flex-direction: column; gap: 16px; color: var(--preu-texto); max-width: 980px;
}
[data-theme="dark"] .preu, .dark .preu {
  --preu-card: #13201a; --preu-borda: #21362b; --preu-texto: #e7f1ea; --preu-suave: #93a89c;
  --preu-fundo2: #182a21; --preu-verde: #34d27e; --preu-verde-claro: rgba(52, 210, 126, 0.12); --preu-erro: #f87171;
}
.preu-card { background: var(--preu-card); border: 1px solid var(--preu-borda); border-radius: 14px; padding: 18px; }
.preu-tit { display: flex; align-items: center; gap: 10px; margin: 0 0 4px; font-size: 16px; font-weight: 700; }
.preu-tit svg { flex: 0 0 auto; width: 18px; height: 18px; padding: 7px; box-sizing: content-box; border-radius: 9px; background: var(--preu-verde-claro); color: var(--preu-verde); }
.preu-sub { margin: 0 0 14px; font-size: 13.5px; color: var(--preu-suave); line-height: 1.45; }
.preu-rot { display: block; margin: 14px 0 6px; font-size: 12px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase; color: var(--preu-suave); }
.preu-dias { display: flex; gap: 6px; overflow-x: auto; padding-bottom: 4px; }
.preu-dia, .preu-hora {
  font: inherit; cursor: pointer; border: 1px solid var(--preu-borda); background: var(--preu-card); color: var(--preu-texto);
  border-radius: 10px; transition: border-color .15s, background .15s;
}
.preu-dia { flex: 0 0 auto; display: flex; flex-direction: column; align-items: center; min-width: 62px; padding: 7px 8px; }
.preu-dia small { font-size: 11px; text-transform: uppercase; color: var(--preu-suave); font-weight: 600; }
.preu-dia strong { font-size: 17px; line-height: 1.2; }
.preu-dia span { font-size: 10.5px; color: var(--preu-suave); }
.preu-horas { display: flex; flex-wrap: wrap; gap: 6px; }
.preu-hora { padding: 7px 12px; font-size: 13.5px; font-variant-numeric: tabular-nums; }
.preu-dia:hover, .preu-hora:hover { border-color: var(--preu-verde); }
.preu-dia.ativo, .preu-hora.ativo { border-color: var(--preu-verde); background: var(--preu-verde-claro); box-shadow: inset 0 0 0 1px var(--preu-verde); }
.preu-campos { display: grid; grid-template-columns: 1fr; gap: 10px; margin-top: 14px; }
.preu-campo { display: flex; flex-direction: column; gap: 5px; font-size: 13px; font-weight: 600; }
.preu-campo input, .preu-campo textarea {
  font: inherit; font-weight: 400; padding: 9px 11px; border-radius: 10px;
  border: 1px solid var(--preu-borda); background: var(--preu-fundo2); color: var(--preu-texto);
}
.preu-campo input:focus, .preu-campo textarea:focus { outline: 2px solid var(--preu-verde); outline-offset: -1px; }
.preu-req { color: var(--preu-erro); }
.preu-rodape { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; margin-top: 14px; }
.preu-escolha { font-size: 13.5px; color: var(--preu-suave); }
.preu-escolha strong { color: var(--preu-texto); }
.preu-btn {
  font: inherit; font-weight: 600; font-size: 14px; cursor: pointer; border-radius: 10px; padding: 9px 16px;
  border: 1px solid var(--preu-verde); background: var(--preu-verde); color: #fff;
}
[data-theme="dark"] .preu-btn, .dark .preu-btn { color: #06351e; }
.preu-btn:disabled { opacity: .55; cursor: default; }
.preu-btn-sec { background: transparent; color: var(--preu-texto); border-color: var(--preu-borda); font-weight: 500; font-size: 13px; padding: 6px 11px; }
[data-theme="dark"] .preu-btn-sec, .dark .preu-btn-sec { color: var(--preu-texto); }
.preu-btn-sec:hover { border-color: var(--preu-verde); }
.preu-erro { margin-top: 12px; padding: 9px 12px; border-radius: 10px; font-size: 13px; color: var(--preu-erro); border: 1px solid currentColor; }
.preu-ok { margin-top: 12px; padding: 10px 12px; border-radius: 10px; font-size: 13.5px; background: var(--preu-verde-claro); border: 1px solid var(--preu-borda); }
.preu-vazio { margin: 0; font-size: 13.5px; color: var(--preu-suave); }
.preu-lista { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 8px; }
.preu-item { display: flex; gap: 12px; align-items: flex-start; padding: 11px 12px; border-radius: 12px; border: 1px solid var(--preu-borda); background: var(--preu-fundo2); }
.preu-item.passou { opacity: .6; }
.preu-quando { flex: 0 0 64px; text-align: center; border-radius: 10px; padding: 5px 4px; background: var(--preu-card); border: 1px solid var(--preu-borda); }
.preu-quando small { display: block; font-size: 10.5px; text-transform: uppercase; color: var(--preu-suave); font-weight: 600; }
.preu-quando strong { display: block; font-size: 15px; }
.preu-quando span { display: block; font-size: 12px; font-variant-numeric: tabular-nums; }
.preu-info { flex: 1 1 auto; min-width: 0; display: flex; flex-direction: column; gap: 3px; font-size: 13.5px; }
.preu-info > strong { font-weight: 600; }
.preu-info p { margin: 0; font-size: 13px; color: var(--preu-suave); white-space: pre-wrap; }
.preu-info a { color: var(--preu-verde); font-weight: 600; }
.preu-sit { align-self: flex-start; display: inline-flex; align-items: center; gap: 6px; font-size: 12px; font-weight: 600; padding: 2px 9px; border-radius: 999px; border: 1px solid var(--preu-borda); }
.preu-sit i { width: 7px; height: 7px; border-radius: 50%; background: var(--preu-suave); }
.preu-sit.aguardando { border-style: dashed; border-color: var(--preu-verde); }
.preu-sit.aguardando i { background: transparent; border: 1.5px solid var(--preu-verde); }
.preu-sit.confirmada { color: var(--preu-verde); }
.preu-sit.confirmada i { background: var(--preu-verde); }
.preu-acoes { flex: 0 0 auto; display: flex; flex-direction: column; gap: 6px; align-items: flex-end; }
.preu-fechada { padding: 12px; border-radius: 10px; background: var(--preu-fundo2); font-size: 13.5px; color: var(--preu-suave); }
@media (max-width: 640px) {
  .preu-card { padding: 14px; }
  .preu-item { flex-wrap: wrap; }
  .preu-acoes { flex-direction: row; width: 100%; justify-content: flex-end; }
}

/* O aviso flutuante (qualquer tela do sistema). */
.preu-aviso {
  position: fixed; right: 20px; bottom: 20px; z-index: 9999; max-width: min(420px, calc(100vw - 32px));
  display: flex; gap: 12px; align-items: flex-start; padding: 14px 16px; border-radius: 14px;
  background: #ffffff; color: #13241b; border: 1px solid #dde8e1; border-left: 3px solid #0c7a3f;
  box-shadow: 0 10px 30px rgba(6, 53, 30, .18); font-size: 13.5px; line-height: 1.45;
  animation: preuEntra .25s ease-out;
}
[data-theme="dark"] .preu-aviso, .dark .preu-aviso { background: #13201a; color: #e7f1ea; border-color: #21362b; border-left-color: #34d27e; box-shadow: 0 10px 30px rgba(0,0,0,.5); }
.preu-aviso strong { display: block; font-size: 14px; margin-bottom: 2px; }
.preu-aviso p { margin: 0; opacity: .85; white-space: pre-wrap; }
.preu-aviso-acoes { display: flex; gap: 8px; margin-top: 10px; }
.preu-aviso button { font: inherit; font-size: 12.5px; cursor: pointer; border-radius: 8px; padding: 5px 11px; border: 1px solid currentColor; background: transparent; color: inherit; opacity: .9; }
.preu-aviso button.principal { background: #0c7a3f; border-color: #0c7a3f; color: #fff; opacity: 1; }
@keyframes preuEntra { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
@media (max-width: 640px) { .preu-aviso { left: 16px; right: 16px; bottom: 16px; } }
`;

const DIAS = ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"];
const MESES = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
const deIso = (s: string) => {
  const [a, m, d] = s.slice(0, 10).split("-").map(Number);
  return new Date(a, m - 1, d);
};
const dataBr = (s: string) => s.slice(0, 10).split("-").reverse().join("/");

function Estilo() {
  return <style>{CSS}</style>;
}

async function chamar(c: Conexao, caminho: string, init?: RequestInit) {
  const r = await fetch((c.base ?? API_PADRAO) + caminho, {
    ...init,
    headers: {
      "content-type": "application/json",
      "x-sercamp-usuario": c.usuario,
      ...(c.cabecalhos?.() ?? {}),
    },
  });
  const corpo = await r.json().catch(() => ({}));
  if (!r.ok) {
    const e = new Error(corpo?.error ?? "Não foi possível falar com o servidor.") as Error & { dias?: Dia[] };
    e.dias = corpo?.dias;
    throw e;
  }
  return corpo;
}

const situacao = (p: Pedido) =>
  p.status === "Solicitada"
    ? { classe: "aguardando", texto: "Aguardando resposta" }
    : p.status === "Marcada"
      ? { classe: "confirmada", texto: p.passou ? "Aconteceu" : "Confirmada" }
      : p.status === "Recusada"
        ? { classe: "recusada", texto: "Não confirmada" }
        : p.status === "Realizada"
          ? { classe: "feita", texto: "Realizada" }
          : { classe: "cancelada", texto: "Cancelada" };

/* ------------------------------------------------------------------ */

export default function PedirReuniaoView({ usuario, base, cabecalhos }: Conexao) {
  const conexao: Conexao = { usuario, base, cabecalhos };
  const [agenda, setAgenda] = useState<{ fechada: boolean; instrucao: string | null; duracao_min?: number; dias: Dia[] } | null>(null);
  const [pedidos, setPedidos] = useState<Pedido[]>([]);
  const [erroCarga, setErroCarga] = useState<string | null>(null);
  const [dia, setDia] = useState<string | null>(null);
  const [hora, setHora] = useState<string | null>(null);
  const [assunto, setAssunto] = useState("");
  const [pauta, setPauta] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    try {
      const [h, m] = await Promise.all([
        chamar(conexao, "/horarios"),
        chamar(conexao, ""),
      ]);
      setAgenda(h);
      setPedidos(Array.isArray(m) ? m : []);
      marcarVistos(usuario, Array.isArray(m) ? m : []);
      setErroCarga(null);
    } catch (e) {
      setErroCarga(e instanceof Error ? e.message : "Não foi possível carregar a agenda.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usuario, base]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  // O dia escolhido some da lista (alguém pegou)? Volta para o primeiro que existe.
  const diaAtual = useMemo(() => agenda?.dias.find((d) => d.data === dia) ?? null, [agenda, dia]);
  useEffect(() => {
    if (agenda && agenda.dias.length > 0 && !diaAtual) {
      setDia(agenda.dias[0].data);
      setHora(null);
    }
  }, [agenda, diaAtual]);

  async function enviar() {
    setErro(null);
    setOk(null);
    if (!dia || !hora) return setErro("Escolha o dia e o horário.");
    if (!assunto.trim()) return setErro("Conte em poucas palavras o assunto da reunião.");
    setEnviando(true);
    // Pede licença para avisar por aqui também (só funciona dentro do clique).
    try {
      if ("Notification" in window && Notification.permission === "default") {
        void Notification.requestPermission();
      }
    } catch {
      /* navegador sem notificação — o aviso na tela continua valendo */
    }
    try {
      const r = await chamar(conexao, "", {
        method: "POST",
        body: JSON.stringify({ data: dia, hora, assunto: assunto.trim(), pauta: pauta.trim() }),
      });
      setOk(r?.mensagem ?? "Pedido enviado.");
      setAssunto("");
      setPauta("");
      setHora(null);
      await carregar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : "Não foi possível enviar.");
      const dias = (e as { dias?: Dia[] }).dias;
      if (dias && agenda) setAgenda({ ...agenda, dias });
      setHora(null);
    } finally {
      setEnviando(false);
    }
  }

  async function cancelar(p: Pedido) {
    const texto =
      p.status === "Marcada" ? "Desmarcar a reunião de " : "Cancelar o pedido de ";
    if (!confirm(texto + dataBr(p.data) + " às " + p.hora + "?")) return;
    try {
      await chamar(conexao, "/" + p.id + "/cancelar", { method: "POST", body: "{}" });
      await carregar();
    } catch (e) {
      alert(e instanceof Error ? e.message : "Não foi possível cancelar.");
    }
  }

  const ativos = pedidos.filter((p) => p.status === "Solicitada" || (p.status === "Marcada" && !p.passou));

  return (
    <div className="preu">
      <Estilo />

      <section className="preu-card">
        <h3 className="preu-tit">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="3" y="4" width="18" height="18" rx="2" />
            <path d="M16 2v4M8 2v4M3 10h18M9 16l2 2 4-4" />
          </svg>
          Reunião com o Desenvolvimento
        </h3>
        <p className="preu-sub">
          Escolha um horário livre e conte o assunto. O desenvolvedor confirma ou recusa, e a
          resposta aparece aqui{ativos.length ? "" : " embaixo"} — o horário fica reservado para você enquanto isso.
          {agenda?.instrucao ? " " + agenda.instrucao : ""}
        </p>

        {erroCarga ? (
          <div className="preu-erro">
            {erroCarga}{" "}
            <button className="preu-btn preu-btn-sec" onClick={carregar}>
              Tentar de novo
            </button>
          </div>
        ) : !agenda ? (
          <p className="preu-vazio">Carregando os horários…</p>
        ) : agenda.fechada ? (
          <div className="preu-fechada">A agenda do Desenvolvimento está fechada no momento.</div>
        ) : agenda.dias.length === 0 ? (
          <div className="preu-fechada">
            Não há horário livre nos próximos dias. Tente de novo mais tarde ou use a Solicitação de
            Atualização.
          </div>
        ) : (
          <>
            <span className="preu-rot">Dia</span>
            <div className="preu-dias">
              {agenda.dias.map((d) => {
                const dt = deIso(d.data);
                return (
                  <button
                    key={d.data}
                    className={"preu-dia" + (d.data === dia ? " ativo" : "")}
                    onClick={() => {
                      setDia(d.data);
                      setHora(null);
                    }}
                  >
                    <small>{DIAS[dt.getDay()]}</small>
                    <strong>{dt.getDate()}</strong>
                    <span>{MESES[dt.getMonth()]}</span>
                  </button>
                );
              })}
            </div>

            <span className="preu-rot">Horário{agenda.duracao_min ? " · " + agenda.duracao_min + " min" : ""}</span>
            <div className="preu-horas">
              {(diaAtual?.horarios ?? []).map((h) => (
                <button key={h} className={"preu-hora" + (h === hora ? " ativo" : "")} onClick={() => setHora(h)}>
                  {h}
                </button>
              ))}
            </div>

            <div className="preu-campos">
              <label className="preu-campo">
                <span>
                  Assunto <span className="preu-req">*</span>
                </span>
                <input
                  value={assunto}
                  maxLength={200}
                  onChange={(e) => setAssunto(e.target.value)}
                  placeholder="Ex.: ajuste no relatório de férias"
                />
              </label>
              <label className="preu-campo">
                <span>O que você quer tratar (opcional)</span>
                <textarea rows={3} value={pauta} onChange={(e) => setPauta(e.target.value)} />
              </label>
            </div>

            <div className="preu-rodape">
              <span className="preu-escolha">
                {dia && hora ? (
                  <>
                    Pedido para <strong>{DIAS[deIso(dia).getDay()]}, {dataBr(dia)} às {hora}</strong>
                  </>
                ) : (
                  "Escolha o dia e o horário."
                )}
              </span>
              <button className="preu-btn" onClick={enviar} disabled={enviando}>
                {enviando ? "Enviando…" : "Pedir reunião"}
              </button>
            </div>
          </>
        )}
        {erro && <div className="preu-erro">{erro}</div>}
        {ok && <div className="preu-ok">{ok}</div>}
      </section>

      <section className="preu-card">
        <h3 className="preu-tit">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M22 12h-6l-2 3h-4l-2-3H2" />
            <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
          </svg>
          Meus pedidos
        </h3>
        {pedidos.length === 0 ? (
          <p className="preu-vazio">Você ainda não pediu nenhuma reunião.</p>
        ) : (
          <ul className="preu-lista">
            {pedidos.map((p) => {
              const s = situacao(p);
              const dt = deIso(p.data);
              const podeCancelar = !p.passou && (p.status === "Solicitada" || p.status === "Marcada");
              return (
                <li key={p.id} className={"preu-item" + (p.passou ? " passou" : "")}>
                  <div className="preu-quando">
                    <small>{DIAS[dt.getDay()]}</small>
                    <strong>
                      {dt.getDate()} {MESES[dt.getMonth()]}
                    </strong>
                    <span>{p.hora ?? "—"}</span>
                  </div>
                  <div className="preu-info">
                    <span className={"preu-sit " + s.classe}>
                      <i /> {s.texto}
                    </span>
                    <strong>{p.titulo}</strong>
                    {p.status === "Recusada" && p.resposta && <p>Motivo: {p.resposta}</p>}
                    {p.status === "Marcada" && p.resposta && <p>{p.resposta}</p>}
                    {p.status === "Marcada" && (p.local || p.link) && (
                      <p>
                        {p.local ? "Local: " + p.local : ""}
                        {p.local && p.link ? " · " : ""}
                        {p.link && (
                          <a href={p.link} target="_blank" rel="noreferrer">
                            entrar na chamada
                          </a>
                        )}
                      </p>
                    )}
                  </div>
                  <div className="preu-acoes">
                    {podeCancelar && (
                      <button className="preu-btn preu-btn-sec" onClick={() => cancelar(p)}>
                        {p.status === "Marcada" ? "Desmarcar" : "Cancelar pedido"}
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}

/* ================================================================== *
 *  O AVISO FLUTUANTE — "sua reunião foi confirmada" em qualquer tela  *
 * ================================================================== */

const chaveVistos = (usuario: string) => "sercamp-reuniao-vistos:" + usuario.toLowerCase();

function lerVistos(usuario: string): Record<string, string> {
  try {
    return JSON.parse(localStorage.getItem(chaveVistos(usuario)) ?? "{}") ?? {};
  } catch {
    return {};
  }
}

/** Tudo o que a pessoa já está vendo na lista conta como "visto". */
function marcarVistos(usuario: string, pedidos: Pedido[]) {
  try {
    const vistos = lerVistos(usuario);
    for (const p of pedidos) vistos[p.id] = p.status;
    localStorage.setItem(chaveVistos(usuario), JSON.stringify(vistos));
  } catch {
    /* aba anônima — o aviso pode repetir, mas nada quebra */
  }
}

/**
 * Consulta os pedidos da pessoa de tempos em tempos e avisa quando um deles foi
 * respondido (confirmado ou recusado) — mesmo que ela esteja em outra tela.
 * Só avisa resposta dos últimos 7 dias, e cada resposta uma vez só.
 */
export function AvisoReuniao({
  usuario,
  onAbrir,
  base,
  cabecalhos,
}: {
  usuario: string | null | undefined;
  onAbrir?: () => void;
  base?: string;
  cabecalhos?: () => Record<string, string>;
}) {
  const [novidade, setNovidade] = useState<Pedido | null>(null);

  const verificar = useCallback(async () => {
    if (!usuario) return;
    try {
      const lista = (await chamar({ usuario, base, cabecalhos }, "")) as Pedido[];
      if (!Array.isArray(lista)) return;
      const vistos = lerVistos(usuario);
      const limite = Date.now() - 7 * 86400_000;
      const nova = lista.find(
        (p) =>
          (p.status === "Marcada" || p.status === "Recusada") &&
          p.respondido_em &&
          new Date(p.respondido_em).getTime() > limite &&
          vistos[p.id] !== p.status,
      );
      if (!nova) return;
      setNovidade(nova);
      try {
        if ("Notification" in window && Notification.permission === "granted" && document.hidden) {
          new Notification(
            nova.status === "Marcada" ? "Reunião confirmada" : "Reunião não confirmada",
            { body: nova.titulo + " — " + dataBr(nova.data) + " às " + nova.hora, tag: "reuniao-" + nova.id },
          );
        }
      } catch {
        /* celular sem suporte ao Notification direto — fica só o aviso na tela */
      }
    } catch {
      /* Desenvolvimento fora do ar — tenta de novo na próxima volta */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usuario, base]);

  useEffect(() => {
    if (!usuario) return;
    const primeira = setTimeout(verificar, 4000);
    const id = setInterval(verificar, 120_000);
    const aoVoltar = () => {
      if (!document.hidden) verificar();
    };
    document.addEventListener("visibilitychange", aoVoltar);
    return () => {
      clearTimeout(primeira);
      clearInterval(id);
      document.removeEventListener("visibilitychange", aoVoltar);
    };
  }, [usuario, verificar]);

  if (!novidade || !usuario) return null;

  const fechar = () => {
    try {
      const vistos = lerVistos(usuario);
      vistos[novidade.id] = novidade.status;
      localStorage.setItem(chaveVistos(usuario), JSON.stringify(vistos));
    } catch {
      /* ignora */
    }
    setNovidade(null);
    setTimeout(verificar, 500); // pode haver outra resposta na fila
  };

  const confirmada = novidade.status === "Marcada";
  return (
    <div className="preu-aviso" role="status">
      <Estilo />
      <div>
        <strong>{confirmada ? "Sua reunião foi confirmada" : "Sua reunião não foi confirmada"}</strong>
        <p>
          {novidade.titulo} — {dataBr(novidade.data)} às {novidade.hora}
          {!confirmada && novidade.resposta ? "\nMotivo: " + novidade.resposta : ""}
          {confirmada && novidade.link ? "\nLink da chamada disponível em Meus pedidos." : ""}
        </p>
        <div className="preu-aviso-acoes">
          {onAbrir && (
            <button
              className="principal"
              onClick={() => {
                fechar();
                onAbrir();
              }}
            >
              {confirmada ? "Ver detalhes" : "Escolher outro horário"}
            </button>
          )}
          <button onClick={fechar}>Ok</button>
        </div>
      </div>
    </div>
  );
}
